import { createFileRoute } from '@tanstack/react-router';

// Public-facing mirror of docs/security/pqc.md and docs/plan.md §4 / §4.12.
// Wording follows the project's "honest technical, not marketing" voice:
// state limits plainly, no softening. The data-testid on the hybrid-PQ
// section is used by the Playwright spec.
function SecurityPage() {
  return (
    <main className="mx-auto max-w-2xl space-y-8 p-8 text-sm leading-relaxed text-text-2">
      <header>
        <h1 className="font-display text-3xl text-text-1">Security &amp; post-quantum posture</h1>
        <p className="mt-1 text-text-3">Last reviewed: 2026-05-20</p>
      </header>

      <section>
        <h2 className="mb-2 font-display text-lg text-text-1">What we encrypt</h2>
        <p>
          Everything you store — notes, links, snippets, folder names, tag names — is encrypted on
          your device with AES-256-GCM before it leaves your browser. The server stores ciphertext
          only. AES-256 is unaffected by Shor&apos;s algorithm and is considered post-quantum safe.
        </p>
      </section>

      <section>
        <h2 className="mb-2 font-display text-lg text-text-1">How your vault is unlocked</h2>
        <p className="mb-2">
          Your Vault Key (the master key that decrypts everything else) is wrapped on the server in
          three independent envelopes. Any one of them, with the right secret, opens the vault:
        </p>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <strong>Daily passphrase</strong> (primary). You choose it at signup. Argon2id — a
            memory-hard, post-quantum-safe key derivation function — turns the passphrase into a key
            that wraps the Vault Key. Used on every fresh-tab unlock unless fast unlock is available
            on this device.
          </li>
          <li>
            <strong>Recovery seed</strong> (lifeline). Generated once at signup, 24 BIP39 words. You
            store it in a password manager or on paper. Used only if you forget the daily
            passphrase.
          </li>
          <li>
            <strong>Passkey PRF</strong> (per-device accelerator, when supported). On devices where
            your passkey returns PRF output, we additionally wrap the Vault Key with a key derived
            from PRF. Unlock on that device becomes one biometric tap. If your authenticator
            doesn&apos;t support PRF — for example, Apple iCloud Keychain currently doesn&apos;t —
            we silently fall back to the daily passphrase. Nothing breaks.
          </li>
        </ul>
        <p className="mt-3">
          Your passkey&apos;s primary job is <em>authentication</em>: proving you&apos;re you to the
          server. Encryption keys are a separate layer. An attacker with your passkey but not your
          passphrase can authenticate but the ciphertext is still useless to them; an attacker with
          your passphrase but not a passkey can&apos;t even sign in.
        </p>
      </section>

      <section data-testid="hybrid-pq-section">
        <h2 className="mb-2 font-display text-lg text-text-1">Hybrid post-quantum protection</h2>
        <ul className="list-disc pl-5 space-y-2">
          <li>
            <strong>Shared items</strong> wrap the Item Key with a hybrid construction:
            RSA-OAEP-2048 + ML-KEM-768 (NIST FIPS 203), combined via HKDF-SHA256. An attacker must
            break <em>both</em> algorithms to recover the key.
          </li>
          <li>
            <strong>TLS in transit</strong> uses Cloudflare&apos;s X25519MLKEM768 hybrid key
            exchange. Post-quantum safe by inheritance.
          </li>
        </ul>
      </section>

      <section>
        <h2 className="mb-2 font-display text-lg text-text-1">What remains classical</h2>
        <p>
          Passkey signatures (ECDSA P-256) authenticate you in real time and do not protect stored
          ciphertext. When WebAuthn authenticators ship post-quantum signature schemes (ML-DSA /
          SLH-DSA), the upgrade is automatic — no change on our side.
        </p>
      </section>

      <section>
        <h2 className="mb-2 font-display text-lg text-text-1">Our migration plan</h2>
        <p>
          We don&apos;t promise quantum security forever. We promise to migrate on the schedule
          dictated by NIST and major standards bodies, and to communicate publicly when we do. When
          RSA-2048 deprecation is recommended, we will ship a v2 share-wrap format that drops the
          classical half and re-encrypt under it — client-side, on next login, because we don&apos;t
          hold your keys.
        </p>
      </section>

      <footer className="border-t border-border pt-4 text-xs text-text-3">
        Deeper technical detail (post-quantum design notes, the Cryptography Bill of Materials) will
        be available on this site as it&apos;s ready for publication.
      </footer>
    </main>
  );
}

export const Route = createFileRoute('/_public/security')({
  component: SecurityPage,
});
